import { useContext } from 'react';
import { useTranslation } from 'react-i18next';
import { Marker, Tooltip } from 'react-leaflet';
import L, { LatLng, LatLngExpression, Polyline } from 'leaflet';
import { TransitEnterexit, HourglassEmpty } from '@mui/icons-material';
import { Divider, Stack, Typography } from '@mui/material';
import simplify from 'simplify-js';

import { calcSections } from './sections';
import { getStartPin, getFinishPin, getNumPin, getEventSvg } from './RouteMarksers';
import { fulldate, fullDateNoYear } from '@/utilities/dateUtils';
import { PlutoPolylineDecorator } from './PlutoPolylineDecorator';
import { RoutePolyline } from './RoutePolyline';
import { isValidLatLong } from '@/utilities/latlong';
import { ctxRoutes, RoutesContextType } from '@/contexts/RoutesCtx';
import { timeDurationShort } from '@/utilities/timeUtils';
import { numberZeroOrBlank } from '@/utilities/preconditions';
import FlagsWithGeoAdress from '@/components/common/FlagsWithGeoAddr';
import { DetailedRoute } from '@/generated/model/detailedRoute';

import './Route.css';
import { DetailedEvent, MinimalStatus, MinimalStatusCollection } from '@/generated/model';

const MINIMAL_DISTANCE = 30;
const BIG_DISTANCE = 200;

interface Point {
  x: number;
  y: number;
}

function getPoints(p: Polyline): Point[] {
  const x = p.getLatLngs()[0] as LatLng[];
  return x.map((ll) => {
    const lll = ll;
    return {
      x: lll.lat,
      y: lll.lng,
    };
  });
}

function getPolyline(p: Point[]): LatLng[] {
  return p.map((pp) => new LatLng(pp.x, pp.y));
}

function DisplayEvents({
  events,
  eventFlag,
}: {
  events: DetailedEvent[] | undefined;
  eventFlag: L.DivIcon | undefined;
}) {
  const { t } = useTranslation();
  return (
    <>
      {events?.map((event) => {
        if (!event || !isValidLatLong(event.position)) return;
        const eventPosition: LatLngExpression = [event.position.latitude, event.position.longitude];
        return (
          <Marker
            key={event.eventTimeMicros}
            position={eventPosition}
            icon={eventFlag}
            eventHandlers={{
              mouseover: (event) => event.target.openPopup(),
              mouseout: (event) => event.target.closePopup(),
            }}
          >
            <Tooltip offset={[10, -5]} className="route-tooltip" data-qa-element-id="route_event_tooltip">
              <Stack>
                <Typography variant="body2" fontWeight="700">
                  {event.description}
                </Typography>
                <Typography variant="body2">{fulldate(event.eventTimeMicros, t)}</Typography>
              </Stack>
            </Tooltip>
          </Marker>
        );
      })}
    </>
  );
}

function DisplayInfo({
  route,
  previousRoute,
  icon,
  finish,
  stopPosition,
  isPoiEnabled,
}: {
  route: DetailedRoute | undefined;
  previousRoute: DetailedRoute | undefined;
  icon: L.DivIcon | undefined;
  finish: L.DivIcon | undefined;
  stopPosition: LatLngExpression | undefined;
  isPoiEnabled: boolean;
}) {
  const { t, i18n } = useTranslation();

  if (!route || !route.startPosition || !stopPosition) return null;

  const startPos: LatLngExpression = [route.startPosition.latitude, route.startPosition.longitude];

  return (
    <>
      <Marker
        key={`${route.id}-start`}
        position={startPos}
        icon={icon}
        riseOnHover
        eventHandlers={{
          mouseover: (event) => event.target.openPopup(),
          mouseout: (event) => event.target.closePopup(),
        }}
      >
        <Tooltip offset={[10, -25]} className="route-tooltip" data-qa-element-id="route_tooltip">
          <div>
            <div className="tooltip-position">
              <FlagsWithGeoAdress
                countryCode={route.startPosition.countryCode}
                countryName={route.startPosition.countryName}
                geocodedAddress={
                  isPoiEnabled && route.startPosition.poi
                    ? route.startPosition.poi
                    : route.startPosition.geocodedAddress
                }
              />
            </div>
            <Divider />
            {previousRoute && previousRoute.stopPosition && (
              <div className="tooltip-arrival" data-qa-element-id="arrival_row">
                <>
                  <TransitEnterexit
                    className="tooltip-icons tooltip-transit-arrival-icon"
                    data-qa-element-id="route_icon"
                  />
                  <Typography variant="subtitle2">
                    {fullDateNoYear(previousRoute.stopPosition.timeMicros, i18n.language, t)}
                  </Typography>
                </>
              </div>
            )}
            {previousRoute && (
              <div className="tooltip-standing-time" data-qa-element-id="standing_time_row">
                <>
                  <HourglassEmpty className="tooltip-icons" data-qa-element-id="hourglass_icon" />
                  <Typography variant="subtitle2">
                    {numberZeroOrBlank(previousRoute.standTimeMicros)
                      ? t('general.data.outOfInterval')
                      : timeDurationShort((key) => t(key), previousRoute.standTimeMicros)}
                  </Typography>
                </>
              </div>
            )}
            <div className="tooltip-departure" data-qa-element-id="departure_time_row">
              <TransitEnterexit className="tooltip-icons" data-qa-element-id="route_icon" />
              <Typography variant="subtitle2">
                {fullDateNoYear(route.startPosition.timeMicros, i18n.language, t)}
              </Typography>
            </div>
          </div>
        </Tooltip>
      </Marker>

      {finish && route.stopPosition && (
        <Marker
          key={`${route.id}-finish`}
          position={stopPosition}
          icon={finish}
          riseOnHover
          eventHandlers={{
            mouseover: (event) => event.target.openPopup(),
            mouseout: (event) => event.target.closePopup(),
          }}
        >
          <Tooltip offset={[10, -25]} className="route-tooltip" data-qa-element-id="route_tooltip">
            <FlagsWithGeoAdress
              countryCode={route.stopPosition.countryCode}
              countryName={route.stopPosition.countryName}
              geocodedAddress={
                isPoiEnabled && route.stopPosition.poi ? route.stopPosition.poi : route.stopPosition.geocodedAddress
              }
            />
            <Divider />
            <div className="tooltip-arrival" data-qa-element-id="arrival_row">
              <TransitEnterexit
                className="tooltip-icons tooltip-transit-arrival-icon"
                data-qa-element-id="route_icon"
              />
              <Typography variant="subtitle2">
                {fullDateNoYear(route.stopPosition.timeMicros, i18n.language, t)}
              </Typography>
            </div>
          </Tooltip>
        </Marker>
      )}
    </>
  );
}

function DisplayRoute({
  route,
  startFlag,
  stopFlag,
  eventFlag,
  nextPosition,
  simplification,
  selectedRoute,
  routeCtx,
  isHighlighted,
}: {
  route: MinimalStatusCollection;
  startFlag: L.DivIcon;
  stopFlag: L.DivIcon | undefined;
  eventFlag: L.DivIcon | undefined;
  nextPosition: MinimalStatus | undefined;
  simplification: number;
  selectedRoute: DetailedRoute | undefined;
  routeCtx: RoutesContextType;
  isHighlighted: boolean;
}) {
  if (!routeCtx.routes) return;
  const positionsFiltered: MinimalStatus[] = route.positions?.filter((p) => isValidLatLong(p)) ?? [];
  const positions: LatLngExpression[] = positionsFiltered.map((p) => [p.latitude, p.longitude]);
  const stopPosition: LatLngExpression = positions[positions.length - 1];

  if (nextPosition && isValidLatLong(nextPosition)) {
    positions.push([nextPosition.latitude, nextPosition.longitude]);
  }

  const sections = calcSections([route]);

  const points = getPoints(sections);
  const simplified = simplify(points, simplification, true);
  const sd = getPolyline(simplified);

  const index = routeCtx.routes.findIndex((route) => route.id === selectedRoute?.id);

  const prevRoute = index > 0 ? routeCtx.routes[index - 1] : undefined;

  function handleClick() {
    routeCtx?.setIsBottomSheetOpen(true);
    routeCtx.setIsBottomSheetUp(true);
    routeCtx?.setAdjustMap(false);
    routeCtx?.setClickedRouteId(route.routeId);
  }

  return (
    <>
      <RoutePolyline positions={sd} isHighlighted={isHighlighted} handleClick={handleClick} />
      {isHighlighted && <PlutoPolylineDecorator p={sd} />}
      <DisplayInfo
        route={selectedRoute}
        previousRoute={prevRoute}
        finish={stopFlag}
        stopPosition={stopPosition}
        icon={startFlag}
        isPoiEnabled={routeCtx.poiNamesEnabled}
      />
      <DisplayEvents events={route.events} eventFlag={eventFlag} />
    </>
  );
}

export function Routes({
  highlightedRoutes,
  grayRoutes,
  zoomSize,
}: {
  highlightedRoutes: MinimalStatusCollection[] | undefined;
  grayRoutes: MinimalStatusCollection[] | undefined;
  zoomSize: number;
}) {
  const ctxRoute = useContext(ctxRoutes);
  if (!highlightedRoutes || !grayRoutes || !ctxRoute) return null;

  const hr = ctxRoute.highlightedRoutes ? ctxRoute.highlightedRoutes : [];
  const markers = createMarkers(highlightedRoutes, hr);

  // mi van a nem folytonos utakkal ?
  // const sections = calcSections(routes);

  function generateKey(r: MinimalStatusCollection) {
    let key: number;
    if (r.positions && r.positions.length) {
      key = r.positions[0].timestamp;
    } else if (r.events && r.events.length) {
      key = r.events[0].eventTimeMicros;
    } else if (r.markers && r.markers.length && r.markers[0].position && isValidLatLong(r.markers[0].position)) {
      key = r.markers[0].position.latitude + r.markers[0].position.longitude;
    } else {
      key = Math.floor(Math.random() * 10000); // FIXME
    }
    return key;
  }

  const selectedRoute = (r: MinimalStatusCollection) =>
    ctxRoute.routes?.find((route) => {
      if (route.id === r.routeId) {
        return route;
      }
    });

  return (
    <>
      {highlightedRoutes.map((r) => {
        const [startMarker, finishMarker, eventMarker, nextPosition] = markers.get(r.routeId);

        return (
          <DisplayRoute
            key={generateKey(r)}
            route={r}
            startFlag={startMarker}
            stopFlag={finishMarker}
            eventFlag={eventMarker}
            nextPosition={nextPosition}
            simplification={zoomSize}
            selectedRoute={selectedRoute(r)}
            routeCtx={ctxRoute}
            isHighlighted={true}
          />
        );
      })}
    </>
  );
}

function createMarkers(routes: MinimalStatusCollection[], highlightedRoutes: number[]) {
  routes.sort(function (a, b) {
    if (!a.positions || !b.positions) {
      return 0;
    }
    if (!a.positions[0] || !b.positions[0]) {
      return 0;
    }
    return a.positions[0].timestamp - b.positions[0].timestamp;
  });
  const markers = new Map();
  let routeIndex = 0;
  for (let i = 0; i < routes.length; i++) {
    const current = routes[i];
    const isHighlighted = highlightedRoutes.length === 0 || highlightedRoutes.includes(current.routeId);
    const startMarker = getPin(getStartPinHtml(routeIndex, isHighlighted));
    const eventMarker = getEventIcon(getEventSvg(isHighlighted));

    const next = i < routes.length - 1 ? routes[i + 1] : undefined;
    const [finishMarker, nexIndex, nextPosition] = calculateFinishMarker(current, next, routeIndex);
    routeIndex = nexIndex;
    markers.set(current.routeId, [startMarker, finishMarker, eventMarker, nextPosition]);
  }
  return markers;
}

function calculateFinishMarker(
  current: MinimalStatusCollection,
  next: MinimalStatusCollection | undefined,
  index: number
): [L.DivIcon | undefined, number, MinimalStatus | undefined] {
  if (!next || !next.positions || !current.positions) {
    return [getPin(getFinishPin()), 0, undefined];
  }
  const nextPosition = next.positions[0];
  const lastPosition = current.positions[current.positions.length - 1];

  if (!isValidLatLong(nextPosition) || !isValidLatLong(lastPosition)) {
    return [getPin(getFinishPin()), 0, undefined];
  }

  const nextPoint = L.latLng(nextPosition.latitude, nextPosition.longitude);
  const lastPoint = L.latLng(lastPosition.latitude, lastPosition.longitude);

  const distance = lastPoint.distanceTo(nextPoint);
  if (distance > BIG_DISTANCE) {
    return [getPin(getFinishPin()), 0, undefined];
  }
  if (distance > MINIMAL_DISTANCE) {
    return [undefined, index + 1, nextPosition];
  }
  return [undefined, index + 1, undefined];
}

function getStartPinHtml(index: number, isHighlighted: boolean) {
  if (index === 0) {
    return getStartPin();
  } else {
    return getNumPin(index, isHighlighted ? 'green' : '#BBB');
  }
}

function getPin(html: string) {
  return L.divIcon({
    html: html,
    iconSize: [32, 48],
    iconAnchor: [16, 48],
  });
}

function getEventIcon(html: string) {
  return L.divIcon({
    html: html,
    className: 'custom-event-icon',
    iconSize: [32, 48],
    iconAnchor: [16, 48],
  });
}
